import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Monitor, CalendarCheck, Check, Phone, Clock, User, ArrowClockwise, Bell } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const map = {
    new: { label: 'Nouvelle', cls: 'bg-rose-100 text-rose-700' },
    treated: { label: 'Traitée', cls: 'bg-emerald-100 text-emerald-700' },
    confirmed: { label: 'Confirmé', cls: 'bg-emerald-100 text-emerald-700' },
    cancelled: { label: 'Annulé', cls: 'bg-slate-200 text-slate-500' },
  };
  const s = map[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>{s.label}</span>;
};

const KioskRdvAdminPage = () => {
  const [kiosk, setKiosk] = useState([]);
  const [rdv, setRdv] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/admin/kiosk`, { withCredentials: true }).then(r => setKiosk(r.data.requests || [])).catch(() => {}),
      axios.get(`${API_URL}/api/admin/rdv`, { withCredentials: true }).then(r => setRdv(r.data.appointments || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const treat = (id) => axios.post(`${API_URL}/api/admin/kiosk/${id}/treat`, {}, { withCredentials: true }).then(load).catch(() => {});
  const cancel = (id) => axios.post(`${API_URL}/api/admin/rdv/${id}/cancel`, {}, { withCredentials: true }).then(load).catch(() => {});

  const newKiosk = kiosk.filter(k => k.status === 'new').length;

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto" data-testid="admin-borne-rdv">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[24px] font-bold text-slate-900 flex items-center gap-2"><Monitor size={24} weight="fill" className="text-rose-500" /> Borne & Rendez-vous</h1>
          <p className="text-slate-500 text-[14px] mt-1">Sélections clients en borne et demandes de rendez-vous.</p>
        </div>
        <button onClick={load} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-[13px] font-semibold flex items-center gap-2"><ArrowClockwise size={15} weight="bold" /> Actualiser</button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* Kiosk selections */}
          <section className="mb-10">
            <h2 className="text-[16px] font-bold text-slate-800 mb-3 flex items-center gap-2">
              Sélections borne
              {newKiosk > 0 && <span className="text-[11px] font-bold text-white bg-rose-500 px-2 py-0.5 rounded-full flex items-center gap-1"><Bell size={11} weight="fill" /> {newKiosk} nouvelle{newKiosk > 1 ? 's' : ''}</span>}
            </h2>
            {kiosk.length === 0 ? (
              <p className="text-slate-400 text-[14px] bg-white rounded-xl border border-slate-100 p-6 text-center">Aucune sélection borne pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {kiosk.map(k => (
                  <div key={k.id} className="bg-white rounded-2xl border border-slate-100 p-4" data-testid={`kiosk-row-${k.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <span className="text-[14px] font-bold text-rose-600 bg-rose-50 px-3 py-1.5 rounded-lg">{k.request_number || '—'}</span>
                        <div>
                          <div className="text-[15px] font-semibold text-slate-900 flex items-center gap-2"><User size={15} weight="fill" className="text-slate-400" /> {k.customer_name}</div>
                          <div className="text-[12px] text-slate-400">{k.customer_phone || 'Pas de téléphone'} · {k.items?.length || 0} paire(s) · {new Date(k.created_at).toLocaleString('fr-FR')}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={k.status} />
                        {k.status === 'new' && (
                          <button onClick={() => treat(k.id)} className="px-3.5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-semibold flex items-center gap-1.5" data-testid={`treat-${k.id}`}>
                            <Check size={14} weight="bold" /> Marquer traité
                          </button>
                        )}
                      </div>
                    </div>
                    {k.items?.length > 0 && (
                      <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                        {k.items.map((it, i) => (
                          <div key={i} className="shrink-0 w-[120px] bg-slate-50 rounded-xl p-2 text-center">
                            <div className="w-full h-[64px] bg-white rounded-lg flex items-center justify-center overflow-hidden mb-1">
                              {it.image && <img src={it.image} alt="" className="w-full h-full object-contain p-1" />}
                            </div>
                            <div className="text-[10.5px] text-slate-600 line-clamp-2 leading-tight">{it.name}</div>
                            {it.price != null && <div className="text-[11px] font-semibold text-slate-800 mt-0.5">{parseFloat(it.price).toFixed(2)} €</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* RDV */}
          <section>
            <h2 className="text-[16px] font-bold text-slate-800 mb-3 flex items-center gap-2"><CalendarCheck size={18} weight="fill" className="text-rose-500" /> Rendez-vous</h2>
            {rdv.length === 0 ? (
              <p className="text-slate-400 text-[14px] bg-white rounded-xl border border-slate-100 p-6 text-center">Aucun rendez-vous.</p>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Date</th>
                      <th className="text-left px-4 py-3 font-semibold">Client</th>
                      <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Prestation</th>
                      <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Mutuelle</th>
                      <th className="text-left px-4 py-3 font-semibold">Statut</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {rdv.map(a => (
                      <tr key={a.id} data-testid={`rdv-row-${a.id}`}>
                        <td className="px-4 py-3"><div className="font-semibold text-slate-800">{new Date(a.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</div><div className="text-slate-400 flex items-center gap-1"><Clock size={11} /> {a.time}</div></td>
                        <td className="px-4 py-3"><div className="font-medium text-slate-800">{a.customer_name}</div><div className="text-slate-400 flex items-center gap-1"><Phone size={11} /> {a.customer_phone}</div>{a.client_status && <span className={`text-[10px] font-semibold ${a.client_status === 'existant' ? 'text-emerald-600' : 'text-amber-600'}`}>{a.client_status === 'existant' ? 'Client existant' : 'Nouveau client'}</span>}</td>
                        <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{a.service_name}<div className="text-slate-400 text-[11px]">{a.practitioner_name}</div></td>
                        <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{a.mutuelle || '—'}</td>
                        <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                        <td className="px-4 py-3 text-right">{a.status !== 'cancelled' && <button onClick={() => cancel(a.id)} className="text-rose-500 hover:text-rose-700 text-[12px] font-semibold">Annuler</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default KioskRdvAdminPage;
